/**
 * Box of a single slider card (including its horizontal margins),
 * in the coordinates of the scrollable content (0 = start of the content).
 */
export type SliderCardBox = {
  left: number
  right: number
}

export type SliderPagingDirection = 'prev' | 'next'

// Sub-pixel tolerance - layout values are fractional
const EPSILON = 1

/**
 * Returns the `scrollLeft` that the slider should scroll to after clicking the arrow,
 * so that a card is never left cut in half "between pages".
 *
 * - `next`: the first card that is not fully visible lands as the first one on the left,
 * - `prev`: the view ends right before the first currently visible card, so every card
 *   that was skipped on the way forward is fully visible again. If that card is cut on the left
 *   (e.g. after the browser clamped the end position), the new view ends with its right edge,
 *   so this card is fully visible too and never skipped.
 *
 * The browser clamps the returned value to the scrollable range.
 */
export function getSliderPageTarget(
  direction: SliderPagingDirection,
  cards: SliderCardBox[],
  scrollLeft: number,
  viewportWidth: number
): number {
  if (!cards.length) return scrollLeft

  if (direction === 'next') {
    const viewportRight = scrollLeft + viewportWidth
    // `left > scrollLeft` guarantees progress also when a card is wider than the viewport
    const target = cards.find(
      (card) => card.right > viewportRight + EPSILON && card.left > scrollLeft + EPSILON
    )
    return target ? target.left : scrollLeft
  }

  const firstVisibleIndex = cards.findIndex((card) => card.right > scrollLeft + EPSILON)
  if (firstVisibleIndex <= 0) return 0

  const reference = cards[firstVisibleIndex]!
  // A card cut on the left is not visible yet - the new view must end with its right edge
  const referenceEdge = reference.left < scrollLeft - EPSILON ? reference.right : reference.left
  const windowStart = referenceEdge - viewportWidth
  const target = cards.find((card) => card.left >= windowStart - EPSILON)

  if (target && target.left < scrollLeft - EPSILON) return target.left

  // The previous card is wider than the viewport - go back by a single card
  return cards[firstVisibleIndex - 1]!.left
}

export type SliderCutEdges = {
  left: boolean
  right: boolean
}

/**
 * Tells on which side of the viewport a card is cut off (partially visible).
 * The slider fades only such an edge - a card that is fully visible must stay fully readable.
 *
 * Pass the card boxes WITHOUT margins (the visible border box), in the content coordinates.
 */
export function getSliderCutEdges(
  cards: SliderCardBox[],
  scrollLeft: number,
  viewportWidth: number
): SliderCutEdges {
  const viewportRight = scrollLeft + viewportWidth

  return {
    left: cards.some(
      (card) => card.left < scrollLeft - EPSILON && card.right > scrollLeft + EPSILON
    ),
    right: cards.some(
      (card) => card.left < viewportRight - EPSILON && card.right > viewportRight + EPSILON
    )
  }
}
